import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import buildIcon from '../../../assets/images/build-icon-gray.svg';
import pencilIcon from '../../../assets/images/pencil.svg';
import clipboardIcon from '../../../assets/images/task-icon-gray.svg';
import ProjectTemplateAdd from '../../../components/ProjectTemplateAdd';
import RelativePopout from '../../../components/RelativePopout';
import ModalBannerConstants from '../../../constants/ModalBannerConstants';
import CollectionDataSource from '../../../dao/DataSource/CollectionDataSource';
import DataSource from '../../../dao/DataSource/DataSource';
import AnalyticsLogger from '../../../helpers/analytics/analyticsLogger';
import dataModel from '../../../models/DataModel';
import ProjectTemplatesService from '../../../services/projectTemplatesService';
import eventBus from '../../../helpers/EventBus';
import { isLatestVersion, pickCreatedVersion } from '../../ProjectTemplatesContainer/ProjectTypesNav/utils/normalizeProjectTemplateVersions';

import './ProjectTemplateHeader.scss';

const PENDING_BACKGROUND_TEMPLATE_KEY = 'pending_background_project_template';

// A template with more builds than the server's threshold gets its copy queued, and the
// request comes back 202 with only a group_identifier -- no id, because the row does not
// exist yet. We watch the group until the new version shows up, then select it. Polling
// rather than waiting on the ActionCable broadcast: the broadcast never arrives without
// Redis (local dev), which is what left this header parked on "CREATING..." with the new
// version missing from the sidebar until someone refreshed by hand.
const BACKGROUND_VERSION_POLL_MS = 2000;
const BACKGROUND_VERSION_POLL_TIMEOUT_MS = 180000;

const deepProjectTemplateDescriptor = {
  project_template: {
    markets: {},
    builds: {
      tasks: {
        completion_criteria: {},
      },
    },
  },
};

const shallowProjectTemplateDescriptor = {
  project_template: {
    markets: {},
    company_closeout_template: {},
  },
};

export class ProjectTemplateHeader extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      editing: false,
      isPublishing: false,
      isLoadingMigratableProjects: false,
      isPreparingNewVersion: false,
      waitingForBackgroundTemplate: false,
    };

    ProjectTemplateHeader.propTypes = {
      projectTemplate: PropTypes.object,
    };
    ProjectTemplateHeader.defaultProps = {
      projectTemplate: {},
    };

    this.createKeyValEntry = this.createKeyValEntry.bind(this);
    this.handleOpenEditCard = this.handleOpenEditCard.bind(this);
    this.handleCloseEditCard = this.handleCloseEditCard.bind(this);
    this.handleUpdateProjectTemplate = this.handleUpdateProjectTemplate.bind(this);
    this.publish = this.publish.bind(this);
    this.discard = this.discard.bind(this);
    this.handleMarketsRetrieved = this.handleMarketsRetrieved.bind(this);
    this.handleProjectTemplateRetrieved = this.handleProjectTemplateRetrieved.bind(this);
    this.handleBackgroundTemplateReady = this.handleBackgroundTemplateReady.bind(this);
    this.handleTemplatePublished = this.handleTemplatePublished.bind(this);
    this.startBackgroundVersionPoll = this.startBackgroundVersionPoll.bind(this);
    this.stopBackgroundVersionPoll = this.stopBackgroundVersionPoll.bind(this);
    this.resolveBackgroundVersion = this.resolveBackgroundVersion.bind(this);

    const currentRole = props.currentUser ? props.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });
  }

  handleMarketsRetrieved(markets, action, acData, fetchedData) {
    if (markets) {
      this.setState({ markets });
    }
  }

  handleProjectTemplateRetrieved(projectTemplate, action, acData, fetchedData) {
    this.setState({ projectTemplate });
  }

  componentDidMount() {
    this._mounted = true;
    this.marketsDataSource = new CollectionDataSource('MarketsCollection', 'Market', { market: {} }, this.handleMarketsRetrieved,
      this.props.currentUser.id, this.props.currentCompany.id);
    this.marketsDataSource.current();

    this.projectTemplateDataSource = new DataSource('ProjectTemplate', this.props.projectTemplate.id, shallowProjectTemplateDescriptor, this.handleProjectTemplateRetrieved);
    this.projectTemplateDataSource.get();

    eventBus.on('projectTemplateBackgroundReady', this.handleBackgroundTemplateReady);
    eventBus.on('projectTemplatePublished', this.handleTemplatePublished);

    // Reloading mid-create must not abandon the wait: pick the watch back up so the user
    // still gets landed on the new version instead of a permanent "CREATING..." button.
    try {
      const raw = sessionStorage.getItem(PENDING_BACKGROUND_TEMPLATE_KEY);
      if (raw) {
        const pending = JSON.parse(raw);
        const currentTypeId = (this.props.projectTemplate && this.props.projectTemplate.project_type_id) || (this.props.projectType && this.props.projectType.id);
        if (pending && pending.project_type_id != null && (pending.project_type_id === currentTypeId || pending.project_type_id === +currentTypeId)) {
          this.setState({ waitingForBackgroundTemplate: true });
          this.startBackgroundVersionPoll(pending.group_identifier, pending.from_version);
        }
      }
    } catch (e) { /* ignore */ }
  }

  handleBackgroundTemplateReady() {
    this.stopBackgroundVersionPoll();
    this.setState({ waitingForBackgroundTemplate: false });
  }

  /**
   * Watch a template group until a version newer than the one we branched from exists, then
   * hand it to resolveBackgroundVersion. Safe to call repeatedly; only one timer runs.
   */
  startBackgroundVersionPoll(groupIdentifier, fromVersion) {
    if (!groupIdentifier || this.backgroundPollTimer) {
      return;
    }
    const branchedFromVersion = Number(fromVersion) || 0;
    const startedAt = Date.now();

    const poll = () => {
      if (!this._mounted) {
        this.stopBackgroundVersionPoll();
        return;
      }
      ProjectTemplatesService.getProjectTemplatesByGroup(groupIdentifier)
        .then(templates => {
          if (!this._mounted || !this.backgroundPollTimer) {
            return;
          }
          const created = pickCreatedVersion(templates, branchedFromVersion);
          if (created) {
            this.resolveBackgroundVersion(created);
            return;
          }
          if (Date.now() - startedAt > BACKGROUND_VERSION_POLL_TIMEOUT_MS) {
            this.stopBackgroundVersionPoll();
            this.clearPendingBackgroundTemplate();
            this.setState({ waitingForBackgroundTemplate: false, isPreparingNewVersion: false });
            dataModel.displayInfoErrorMessage('The new version is still being created. Refresh to see it once it is done.', 6000, 2);
          }
        })
        .catch(() => { /* transient failure: the next tick retries */ });
    };

    this.backgroundPollTimer = setInterval(poll, BACKGROUND_VERSION_POLL_MS);
    poll();
  }

  stopBackgroundVersionPoll() {
    if (this.backgroundPollTimer) {
      clearInterval(this.backgroundPollTimer);
      this.backgroundPollTimer = null;
    }
  }

  clearPendingBackgroundTemplate() {
    try {
      sessionStorage.removeItem(PENDING_BACKGROUND_TEMPLATE_KEY);
    } catch (e) { /* ignore */ }
  }

  /**
   * Land on a version that finished copying in the background: same end state as a
   * synchronous create, so the user sees it in the sidebar, selected, with its
   * PUBLISH/DISCARD controls, rather than having to refresh and click it themselves.
   */
  resolveBackgroundVersion(newVersion) {
    this.stopBackgroundVersionPoll();
    this.clearPendingBackgroundTemplate();
    this.setState({ waitingForBackgroundTemplate: false, isPreparingNewVersion: false });
    if (this.props.handleAddProjectTemplate) {
      this.props.handleAddProjectTemplate(newVersion);
    }
    // Refresh the sidebar first so the version list already contains the new version by the
    // time the route change selects it.
    eventBus.emit('templateVersionCreated');
    const projectTypeId = newVersion.project_type_id ||
      (this.state.projectTemplate && this.state.projectTemplate.project_type_id) ||
      (this.props.projectType && this.props.projectType.id);
    this.props.history.push(`/project-types/${projectTypeId}/project-template/${newVersion.id}`);
    this.analyticsLogger.logProjectTemplateEvent(newVersion, 'ProjectTemplateCreated');
  }

  handleTemplatePublished(payload) {
    const currentId = (this.state.projectTemplate && this.state.projectTemplate.id) ||
      (this.props.projectTemplate && this.props.projectTemplate.id);
    if (!currentId) {
      return;
    }
    // Only refetch when the completed publish is for the template we're showing.
    const publishedId = payload && payload.projectTemplateId;
    if (publishedId != null && publishedId != currentId) {
      return;
    }
    // Force a network fetch so the persisted publishing=false / published_at is
    // reflected, un-sticking a "PUBLISHING..." button when the reload beat the job.
    dataModel.projectTemplatesDAO
      .getProjectTemplate(currentId, { recordDescriptor: shallowProjectTemplateDescriptor, force: true })
      .then(projectTemplate => {
        if (this._mounted && projectTemplate) {
          this.setState({ projectTemplate, isPublishing: false });
        }
      })
      .catch(() => { /* ignore */ });
  }
  
  componentDidUpdate(prevProps) {
    if (prevProps.projectTemplate && this.props.projectTemplate && prevProps.projectTemplate.id != this.props.projectTemplate.id) {
      if (this.projectTemplateDataSource) {
        this.projectTemplateDataSource.release();
      }
  
      this.projectTemplateDataSource = new DataSource('ProjectTemplate', this.props.projectTemplate.id, shallowProjectTemplateDescriptor, this.handleProjectTemplateRetrieved);
      this.projectTemplateDataSource.get();
    }
  }
  
  componentWillUnmount() {
    this._mounted = false;
    this.stopBackgroundVersionPoll();
    eventBus.off('projectTemplateBackgroundReady', this.handleBackgroundTemplateReady);
    eventBus.off('projectTemplatePublished', this.handleTemplatePublished);
    if (this.marketsDataSource) {
      this.marketsDataSource.release();
      this.marketsDataSource = null;
    }
    if (this.projectTemplateDataSource) {
      this.projectTemplateDataSource.release();
      this.projectTemplateDataSource = null;
    }
  }

  discard() {
    const { projectTemplate } = this.state;
    
    dataModel.showGlobalModal('SYSTEM_POPUP', {
      captionText: 'IMPORTANT',
      message: `This action will delete template version ${projectTemplate.version}. Are you sure?`,
      height: 184,
      width: 300,
      cancelButtonText: 'CANCEL',
      okButtonText: 'DISCARD',
      handleOwnerOk: async () => {
        ProjectTemplatesService.deleteProjectTemplate(projectTemplate.id).then(() => {
          eventBus.emit('templateDeleted', { id: projectTemplate.id, group_identifier: projectTemplate.group_identifier, is_template: false, projectTypeId: projectTemplate.project_type_id });
          dataModel.displayInfoErrorMessage('The Template changes have been successfully discarded.', 4000, 1);
        });

      },
      type: 'info',
    });
  }

  publish() {
    const { projectTemplate } = this.state;
    dataModel.inProgress = 600000;
    const affectedProjectsRequest = ProjectTemplatesService.getMigratableProjects(projectTemplate.id);
    const deepTemplateRequest = dataModel.projectTemplatesDAO.getProjectTemplate(projectTemplate.id,
      { recordDescriptor: deepProjectTemplateDescriptor });
    
    if(projectTemplate.markets.length < 1){
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        captionText: 'IMPORTANT',
        message: 'This template does not have any markets. Please add one before publishing the template.',
        height: 184,
        width: 300,
        okButtonText: 'OK',
        okButtonColor: 'green',
        oneButton: true,
        type: 'info',
      });
    }
    else{
      this.setState({ isLoadingMigratableProjects: true });
      Promise.all([affectedProjectsRequest, deepTemplateRequest]).then(responses => {
        dataModel.inProgress = 0;
        const affectedProjects = responses[0];
        const expandedTemplate = responses[1];
        this.setState({ isLoadingMigratableProjects: false });
        if (!affectedProjects || !affectedProjects.length) {
          dataModel.showGlobalModal(ModalBannerConstants.SYSTEM_POPUP, {
            message: 'Are you sure you want to publish \'' + projectTemplate.name + '\'? No projects will be affected.',
            cancelButtonText: 'CANCEL',
            okButtonText: 'YES',
            height: 184,
            width: 320,
            handleOwnerOk: async() => {
              const publishedTemplate = await dataModel.projectTemplatesDAO.publishTemplateVersion(projectTemplate.id, { recordDescriptor: shallowProjectTemplateDescriptor });
              this.analyticsLogger.logProjectTemplateEvent(publishedTemplate, 'ProjectTemplatePublished');
              // No projects to migrate, so the publish is synchronous: reflect the
              // published state (published_at set) in place instead of reloading.
              if (this._mounted && publishedTemplate) {
                this.setState(prev => ({
                  projectTemplate: { ...prev.projectTemplate, ...publishedTemplate },
                  isPublishing: false,
                }));
              }
            },
          });
        }
        else {
          dataModel.showGlobalModal(ModalBannerConstants.TEMPLATE_PUBLISH_MODAL, {
            template: expandedTemplate,
            affectedProjects,
            onPublish: (response) => {

              const { migratedProjects, updatedTemplate, isBackgroundProcess } = response;
              this.analyticsLogger.logProjectTemplateEvent(updatedTemplate, 'ProjectTemplatePublished');
              if (isBackgroundProcess) {
                dataModel.showGlobalModal('SYSTEM_POPUP', {
                  captionText: 'NOTICE',
                  message: 'Your Projects will be updated as you work. You will be notified when updates are complete.',
                  height: 184,
                  width: 300,
                  okButtonText: 'OK',
                  okButtonColor: 'green',
                  oneButton: true,
                  type: 'info',
                  handleOwnerOk: async() => {
                    dataModel.hideGlobalModal();
                    // No page reload: reloading wipes the Redux store, and the
                    // sidebar re-seed only restores num_versions/version/latest,
                    // dropping publishing/published_at so version buttons render
                    // stale. migrateAndPublish already stored publishing=true;
                    // reflect it in the header so the button shows "PUBLISHING...".
                    if (this._mounted) {
                      this.setState(prev => ({
                        projectTemplate: { ...prev.projectTemplate, ...updatedTemplate, publishing: true },
                        isPublishing: false,
                      }));
                    }
                  },
                });
              }
              else {
                dataModel.hideGlobalModal();
                // mark all cached projects as needing sync
                Object.keys(dataModel.projectsDAO.store.getState().projects || {}).forEach(projectId => {
                  const project = dataModel.projectsDAO.findItem(projectId);
                  if (project) {
                    project._recordState = RecordStates.NEEDS_ATTRIBUTE_SYNC;
                    dataModel.projectsDAO.setItemInStore(project);
                  }
                });
                // mark all cached completion criteria as needing sync
                dataModel.collectionsDAO.markCollectionNeedsSync();
                dataModel.showGlobalBanner(ModalBannerConstants.GENERIC_TEXT_BANNER, {
                  text: `${migratedProjects.length} Project${migratedProjects.length == 1 ? ' has' : 's have'} been updated.`,
                  isBottomBanner: true,
                  hasCloseButton: true,
                  className: 'project-templates-bottom-banner',
                });
              }
            },
          });
        }
      });
    }
  }

  createNewVersion(templateId) {
    this.setState({ isPreparingNewVersion: true });
    const branchedFrom = this.state.projectTemplate || this.props.projectTemplate || {};
    dataModel.projectTemplatesDAO.createProjectTemplateVersion(templateId, { recordDescriptor: shallowProjectTemplateDescriptor })
      .then(newVersion => {
        if (newVersion && newVersion.background) {
          // Queued copy: there is no version to select yet. Record enough to resume across a
          // reload, then watch for it. Both paths end the same way -- the new version
          // selected, with its own controls -- so a big template only differs by the wait.
          const groupIdentifier = newVersion.group_identifier || branchedFrom.group_identifier;
          const fromVersion = Number(branchedFrom.version) || 0;
          this.setState({ isPreparingNewVersion: false, waitingForBackgroundTemplate: true });
          try {
            sessionStorage.setItem(PENDING_BACKGROUND_TEMPLATE_KEY, JSON.stringify({
              project_type_id: branchedFrom.project_type_id || (this.props.projectType && this.props.projectType.id),
              group_identifier: groupIdentifier,
              from_version: fromVersion,
              template_name: branchedFrom.name || 'Project template',
            }));
          } catch (e) { /* ignore */ }
          this.startBackgroundVersionPoll(groupIdentifier, fromVersion);
          return;
        }
        this.setState({ isPreparingNewVersion: false });
        if (this.props.handleAddProjectTemplate) {
          this.props.handleAddProjectTemplate(newVersion);
        }
        // Sidebar first, so the version list already holds the new version when the route
        // change selects it.
        eventBus.emit('templateVersionCreated');
        this.props.history.push(`/project-types/${newVersion.project_type_id}/project-template/${newVersion.id}`);
        this.analyticsLogger.logProjectTemplateEvent(newVersion, 'ProjectTemplateCreated');
      })
      .catch(() => {
        this.setState({ isPreparingNewVersion: false });
      });
  }

  /*  * RENDER FUNCTIONS */
  createKeyValEntry(keyText, valText, imgSrc) {
    return <span className="key-val">
      {imgSrc && <img className='key-val__img' src={imgSrc} alt='key'/>}
      <p className='key-val__key'>{keyText}</p>
      <p className='key-val__divider'>|</p>
      <p className='key-val__val'>{valText}</p>
    </span>;
  }

  handleOpenEditCard() {
    this.setState({ editing: true });
  }

  handleCloseEditCard() {
    this.setState({ editing: false });
  }

  handleUpdateProjectTemplate(templateName, templateMarkets, templateCloseoutId, config) {
    let editedProjectTemplate = this.state.projectTemplate;
    if (templateName && templateName != editedProjectTemplate.name) {
      editedProjectTemplate = { ...editedProjectTemplate, ...{ name: templateName } };
    }
    if (templateMarkets) {
      editedProjectTemplate = { ...editedProjectTemplate, ...{ markets: templateMarkets } };
    }
    if (templateCloseoutId) {
      editedProjectTemplate = { ...editedProjectTemplate, ...{ company_closeout_template_id: templateCloseoutId } };
      if (editedProjectTemplate.company_closeout_template) {
        // TODO: this may only be temporary until DAO migration has settled...
        // if this is not done then closeout template change does not stick...
        delete editedProjectTemplate.company_closeout_template;
      }
    }
    if (config) {
      editedProjectTemplate.config = _.merge(editedProjectTemplate.config, config);
    }

    //SOURCEHASPRIORITY
    dataModel.projectTemplatesDAO.updateProjectTemplate(editedProjectTemplate, { recordDescriptor: shallowProjectTemplateDescriptor, sourceHasPriority: true })
      .then((updatedTemplate) => { 
        this.handleCloseEditCard(); 
      });
  }

  createHeaderEdit() {
    const { closeoutTemplates, currentCompany, projectType } = this.props;
    const { projectTemplate } = this.state;
    const { markets } = this.state;
    const templateEditInfo = {
      templateId: projectTemplate.id,
      templateName: projectTemplate.name,
      templateNameEditable: projectTemplate.version == 1 && !projectTemplate.published_at && !projectTemplate.publishing,
      templateCloseout: projectTemplate.company_closeout_template,
      templateMarkets: projectTemplate.markets,
      duration: projectTemplate.config && projectTemplate.config.duration,
    };
    return (
      <div ref={el => this.editTemplateRef = el}>
       <RelativePopout
        top={0}
        left={0}
        verticalOffset={0}
        >
        <ProjectTemplateAdd
          id="project-template-add-card"
          closeoutTemplates={closeoutTemplates}
          closeAddCard={this.handleCloseEditCard}
          selectedCompany={currentCompany}
          markets={markets || []}
          version={projectTemplate.version}
          editing={this.state.editing}
          templateEditInfo={ templateEditInfo }
          existingTemplates={projectType ? projectType.project_templates : null}
          handleUpdateProjectTemplate={this.handleUpdateProjectTemplate}
          handleAddProjectTemplate={this.props.handleAddProjectTemplate}
        />
      </RelativePopout>
    </div>
    );
  }

  render() {
    let { projectTemplate, isPublishing, isPreparingNewVersion, isLoadingMigratableProjects, waitingForBackgroundTemplate } = this.state;
    if (!projectTemplate) {
      projectTemplate = this.props.projectTemplate;
    }

    const markets = projectTemplate.markets || [];
    // const closeoutTemplate = projectTemplate.company_closeout_template || {}

    const marketsListText = markets.reduce((accumulator, val, index) => { 
      return index > 0 ? accumulator + ', ' + val.code : val.code;
    }, '');

    // `latest` from the API is authoritative: only the server knows about discarded
    // versions, which leave permanent gaps in the numbering. Fall back only when the
    // response omitted it, and then compare against the highest version in the group,
    // NEVER a count of versions. A group holding 7 live templates whose newest is
    // version 9 is normal; comparing 9 against 7 stranded that newest version as
    // "View Only" with no way to publish or discard it, while the published version 7
    // of the same group read as newest (7 === 7) and offered NEW VERSION.
    const isMostRecentVersion = projectTemplate.latest !== undefined
      ? projectTemplate.latest
      : isLatestVersion(
        projectTemplate,
        this.props.projectType && this.props.projectType.project_templates
      );

    let headerControl = null;

    if(isMostRecentVersion && !projectTemplate.published_at && !projectTemplate.publishing){
      headerControl = (<div>
        <button onClick={this.discard} className='discard-button'>
          DISCARD
        </button>
        <button onClick={isLoadingMigratableProjects ? null : this.publish } className='publish-button'>
          {isLoadingMigratableProjects ? 'LOADING...' : 'PUBLISH'}
        </button>
      </div>);
    }
    else if(isPublishing || isMostRecentVersion && projectTemplate.publishing){
      headerControl = (<button className={'publish-button publish-button--disabled'}>
        PUBLISHING...
        </button>);
    }
    else if(isPreparingNewVersion || waitingForBackgroundTemplate || isMostRecentVersion && projectTemplate.publishing){
      headerControl = (<button className={'publish-button publish-button--disabled'} disabled>
        {waitingForBackgroundTemplate ? 'CREATING...' : 'CREATING...'}
      </button>);
    }
    else if(isMostRecentVersion && projectTemplate.published_at){
      headerControl = (<button className={'publish-button publish-button--new_version'} onClick={() => this.createNewVersion(projectTemplate.id)}>
        NEW VERSION
      </button>);
    }
    else if(!isMostRecentVersion){
      headerControl = (<p className='view-only'>View Only</p>);
    }

    return <div className='project-template-header'>
      {
        this.state.editing && this.createHeaderEdit()
      }

      <div className='project-template-header__template-info'>
        <span className='name-and-edit'>
          <h2 className='type-name'>{projectTemplate.name || ''}</h2>
          {
            isMostRecentVersion && !projectTemplate.published_at && !projectTemplate.publishing &&
            <img className='edit-button' src={pencilIcon} alt='edit-button' onClick={this.handleOpenEditCard}/>
          }
        </span>
        <span className='info-row'>  
          {this.createKeyValEntry('Builds', projectTemplate.builds_count ? projectTemplate.builds_count - 1 : 0, buildIcon)}
          {this.createKeyValEntry('Markets', marketsListText || '')}
        </span>
        <span className='info-row'>
          {this.createKeyValEntry('Tasks', projectTemplate.tasks_count || 0, clipboardIcon)}
        </span>
      </div>

      <div className='project-template-header__version'>
        <h3 className='version-text'>{'Version ' + projectTemplate.version}</h3>
      </div>

      <div className='project-template-header__controls'>
        {headerControl}
      </div>
    </div>;
  }

}

export default withRouter(ProjectTemplateHeader);



// WEBPACK FOOTER //
// ./src/containers/ProjectTypesContainer/ProjectTemplateHeader/index.js